import { SortingState } from '@tanstack/react-table';
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityAdapter,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit';

import type { RootState } from 'core/store';
import { JobOverview } from 'core/models/job';
import { jobService } from 'core/services/JobService';
import { FetchStatus, sameMembers } from 'core/utils';
import {
  ActiveFilters,
  authorsFilter,
  Filter,
  Filters,
  FilterSelectors,
  FilterToggledAction,
  FilterValue,
  PagingChangedAction,
  PagingState,
  queryFilter,
  repetitionTypeFilter,
  ScheduleDeletedAction,
  ScheduleStatusChange,
  SortingChangedAction,
  statusFilter,
  storesFilter,
} from './types';
import {
  filterBySearchText,
  prepareList as prepareScheduleList,
} from './listLoader';
import { startAppListening } from '../listenerMiddleware';
import { getSelectedFiltersOtherThan, sortByOccurences } from './filterHelpers';

export interface ScheduleListState {
  allSchedules: JobOverview[];
  currentList: JobOverview[];
  shouldReloadList: boolean;
  sortingState: SortingState;
  pagingState: PagingState;
  searchText: string;
  filters: Filters; // all filter vales calculated from the list of overviews
  selectedFilters: ActiveFilters; // these are just selected, before reload
  appliedFilters: ActiveFilters; // these are applied to current rendered list
  appliedSearchText: string; // the search text that is applied to rendered list
  status: FetchStatus;
  error: unknown;
}

const defaultFilterAdapterConfig = {
  selectId: (q: FilterValue) => q.value,
  sortComparer: sortByOccurences,
};

const queriesAdapter = createEntityAdapter<FilterValue, string>(
  defaultFilterAdapterConfig
);
const storesAdapter = createEntityAdapter<FilterValue, string>(
  defaultFilterAdapterConfig
);
const authorAdapter = createEntityAdapter<FilterValue, string>(
  defaultFilterAdapterConfig
);
const statusAdapter = createEntityAdapter<FilterValue, string>(
  defaultFilterAdapterConfig
);
const repetitionTypeAdapter = createEntityAdapter<FilterValue, string>(
  defaultFilterAdapterConfig
);

const filterFromString = (key: string) => {
  switch (key) {
    case 'queries':
      return queryFilter;
    case 'stores':
      return storesFilter;
    case 'authors':
      return authorsFilter;
    case 'repetitionType':
      return repetitionTypeFilter;
    default:
      return statusFilter;
  }
};

const filterAdapterFromString = (key: string) => {
  switch (key) {
    case 'queries':
      return queriesAdapter;
    case 'stores':
      return storesAdapter;
    case 'authors':
      return authorAdapter;
    case 'repetitionType':
      return repetitionTypeAdapter;

    default:
      return statusAdapter;
  }
};

const initialState: ScheduleListState = {
  allSchedules: [],
  currentList: [],
  status: 'idle',
  sortingState: [{ id: 'status', desc: false }],
  pagingState: {
    pageIndex: 0,
    pageSize: 80,
  },
  searchText: '',
  filters: {
    queries: queriesAdapter.getInitialState(),
    stores: storesAdapter.getInitialState(),
    authors: authorAdapter.getInitialState(),
    status: storesAdapter.getInitialState(),
    repetitionType: repetitionTypeAdapter.getInitialState(),
  },
  shouldReloadList: false,
  selectedFilters: {
    queries: [],
    stores: [],
    authors: [],
    status: [],
    repetitionType: [],
  },
  appliedFilters: {
    queries: [],
    stores: [],
    authors: [],
    status: [],
    repetitionType: [],
  },
  appliedSearchText: '',
  error: '',
};

const scheduleListSlice = createSlice({
  name: 'schedules',
  initialState,
  reducers: {
    fetchNewSchedules: (state: ScheduleListState) => {
      state.status = 'idle';
    },
    sortingChanged: (
      state: ScheduleListState,
      sortingChangedAction: PayloadAction<SortingChangedAction>
    ) => {
      state.sortingState = sortingChangedAction.payload.newSort;
    },
    pagingChanged: (
      state: ScheduleListState,
      pagingChangedAction: PayloadAction<PagingChangedAction>
    ) => {
      state.pagingState.pageIndex = pagingChangedAction.payload.pageIndex;
    },
    searchtTextChanged: (
      state: ScheduleListState,
      action: PayloadAction<string>
    ) => {
      state.searchText = action.payload;
      state.shouldReloadList = true;
    },
    scheduleStatusChanged: (
      state: ScheduleListState,
      action: PayloadAction<ScheduleStatusChange>
    ) => {
      const change = action.payload;
      const cachedSchedule = state.allSchedules.find(
        schedule => schedule.id === change.scheduleId
      );
      if (!cachedSchedule) {
        return state;
      }
      const oldStatus = cachedSchedule.status;
      cachedSchedule.status = change.newStatus;
      const renderedSchedule = state.currentList.find(
        schedule => schedule.id === change.scheduleId
      );
      if (renderedSchedule) {
        renderedSchedule.status = change.newStatus;
      }
      const oldStatusFilter = state.filters.status.entities[oldStatus];
      if (oldStatusFilter && oldStatusFilter.baseOccurences) {
        statusAdapter.updateOne(state.filters.status, {
          changes: {
            baseOccurences: oldStatusFilter.baseOccurences - 1,
            occurences: (oldStatusFilter.occurences || 1) - 1,
          },
          id: oldStatusFilter.value,
        });
      }

      const newStatusFilter = state.filters.status.entities[change.newStatus];
      if (!newStatusFilter) {
        statusAdapter.addOne(state.filters.status, {
          label: change.newStatus,
          value: change.newStatus,
          baseOccurences: 1,
          occurences: 1,
        });
        return;
      }
      statusAdapter.updateOne(state.filters.status, {
        changes: {
          baseOccurences: (newStatusFilter.baseOccurences || 0) + 1,
          occurences: (newStatusFilter.occurences || 0) + 1,
        },
        id: newStatusFilter.value,
      });
    },
    scheduleDeleted: (
      state: ScheduleListState,
      action: PayloadAction<ScheduleDeletedAction>
    ) => {
      const schedule = state.allSchedules.find(
        schedule => schedule.id === action.payload.scheduleId
      );
      if (!schedule) {
        return;
      }
      state.allSchedules = state.allSchedules.filter(
        schedule => schedule.id !== action.payload.scheduleId
      );
      state.currentList = state.currentList.filter(
        schedule => schedule.id !== action.payload.scheduleId
      );

      const deleteFilterValueOccurence = (filter: Filter, valueId: string) => {
        const filterValue = filterAdapterFromString(filter.key)
          .getSelectors()
          .selectById(state.filters[filter.key as keyof Filters], valueId);
        if (filterValue) {
          filterAdapterFromString(filter.key).updateOne(
            state.filters[filter.key as keyof Filters],
            {
              changes: {
                baseOccurences: (filterValue?.baseOccurences || 1) - 1,
                occurences: (filterValue?.occurences || 1) - 1,
              },
              id: filterValue?.value,
            }
          );
        }
      };

      Object.keys(state.filters)
        .map(filterFromString)
        .forEach(filter => {
          const valueId = filter.getPickerFunc()(schedule);
          if (typeof valueId === 'string') {
            deleteFilterValueOccurence(filter, valueId);
          } else {
            valueId.forEach(id => {
              deleteFilterValueOccurence(filter, id);
            });
          }
        });
    },
    filterToggled: (state, action: PayloadAction<FilterToggledAction>) => {
      const toggled = action.payload;
      const existingIndex = state.selectedFilters[
        toggled.type as keyof ActiveFilters
      ].findIndex(v => v === toggled.value);
      let newFilters: string[] = [];

      if (toggled.shouldBeSelected) {
        if (existingIndex > -1) {
          // already present in selected filters
          return state;
        }
        newFilters = state.selectedFilters[
          toggled.type as keyof ActiveFilters
        ].concat(toggled.value);
      } else {
        if (existingIndex < 0) {
          // already removed
          return state;
        }
        newFilters = state.selectedFilters[
          toggled.type as keyof ActiveFilters
        ].filter(v => v !== toggled.value);
      }
      const shouldReloadList = !sameMembers(
        newFilters,
        state.appliedFilters[toggled.type as keyof ActiveFilters]
      );
      return {
        ...state,
        shouldReloadList: shouldReloadList,
        selectedFilters: {
          ...state.selectedFilters,
          [toggled.type]: newFilters,
        },
      };
    },
    removeActiveFilter: (
      state,
      action: PayloadAction<{ type: string; value: string }>
    ) => {
      const { type, value } = action.payload;
      const filterKey = type as keyof ActiveFilters;
      const currentSelected = state.selectedFilters[filterKey];

      const selectedToKeep = currentSelected.filter(v => v !== value);
      if (selectedToKeep.length === currentSelected.length) {
        return state;
      }

      const appliedToKeep = state.appliedFilters[filterKey].filter(
        v => v !== value
      );

      return {
        ...state,
        shouldReloadList: true,
        selectedFilters: {
          ...state.selectedFilters,
          [type]: selectedToKeep,
        },
        appliedFilters: {
          ...state.appliedFilters,
          [type]: appliedToKeep,
        },
      };
    },
    filtersReset: state => {
      Object.keys(state.filters)
        .map(key => filterFromString(key))
        .forEach(filter => {
          if (
            Object.keys(getSelectedFiltersOtherThan(filter.key, state)).some(
              // if any of other filters is selected, we reset occurences of this filter
              key =>
                state.selectedFilters[key as keyof ActiveFilters].length > 0
            )
          ) {
            resetToBaseOccurences(
              state.filters[filter.key as keyof Filters],
              filterAdapterFromString(filter.key)
            );
          }
        });
      state.selectedFilters = initialState.selectedFilters;
      state.appliedFilters = initialState.appliedFilters;
      state.shouldReloadList = true;
    },
    reloadList: state => {
      if (!state.shouldReloadList) {
        return state;
      }
      const newList = prepareScheduleList(
        state.allSchedules,
        state.searchText,
        state.selectedFilters
      );

      return {
        ...state,
        shouldReloadList: false,
        pagingState: { ...state.pagingState, pageIndex: 0 },
        currentList: newList,
      };
    },
    reloadFilterValues: state => {
      // an action that serves as an observable. we wait for this action and trigger other asynchronously
      return state;
    },
    calculateFilterValuesOccurences: state => {
      // step 1: we need to know which filters have changed
      const changed: Record<string, boolean> = {};
      Object.keys(state.filters).forEach(key => {
        const hasFilterChanged = !sameMembers(
          state.appliedFilters[key as keyof ActiveFilters],
          state.selectedFilters[key as keyof ActiveFilters]
        );
        changed[key] = hasFilterChanged;
      });
      const hasSearchTextChanged = state.searchText !== state.appliedSearchText;
      // step 2: if nothing changed (filters or search text), return
      if (
        !Object.keys(changed).some(key => changed[key] === true) &&
        !hasSearchTextChanged
      ) {
        return state;
      }

      // step 3: for each filter, check if any other filter changed. If so, recalculate values
      // first pre-filter with search text
      const prefilteredSchedules = filterBySearchText(
        state.searchText,
        state.allSchedules
      );

      Object.keys(changed).forEach(filterName => {
        const needsRecalculating =
          Object.keys(changed).some(
            key => changed[key] === true && key !== filterName
          ) || hasSearchTextChanged;
        if (!needsRecalculating) {
          return;
        }
        const filter = filterFromString(filterName);
        const otherSelectedFilterValues = getSelectedFiltersOtherThan(
          filterName,
          state
        );

        updateFilterOcurences(
          prefilteredSchedules,
          filter.getFilterFunc(state),
          filterAdapterFromString(filter.key),
          filter.getPickerFunc(),
          state.filters[filterName as keyof Filters],
          Object.values(otherSelectedFilterValues),
          state.searchText !== '' ? [state.searchText] : []
        );
      });

      state.appliedFilters = state.selectedFilters;
      state.appliedSearchText = state.searchText;
      return state;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchSchedules.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchSchedules.fulfilled, (state, action) => {
        state.status = 'success';
        state.allSchedules = action.payload;

        queriesAdapter.removeAll(state.filters.queries);
        storesAdapter.removeAll(state.filters.stores);
        authorAdapter.removeAll(state.filters.authors);
        statusAdapter.removeAll(state.filters.status);
        repetitionTypeAdapter.removeAll(state.filters.repetitionType);
        const valueMap: Record<string, Record<string, FilterValue>> =
          Object.keys(state.filters).reduce((obj, key) => {
            obj[key] = {};
            return obj;
          }, {} as Record<string, Record<string, FilterValue>>);

        const updateValueMap = (
          filterType: string,
          valueId: string,
          label: string
        ) => {
          valueMap[filterType][valueId] = valueMap[filterType][valueId]
            ? {
                ...valueMap[filterType][valueId],
                occurences: valueMap[filterType][valueId].occurences + 1,
                baseOccurences: valueMap[filterType][valueId].occurences + 1,
              }
            : {
                value: valueId,
                label: label,
                occurences: 1,
                baseOccurences: 1,
              };
        };

        action.payload.forEach(element => {
          element.stores.forEach(store => {
            updateValueMap('stores', store.id, store.buName);
          });

          Object.keys(state.filters)
            .filter(key => key !== 'stores')
            .forEach(filterName => {
              const filter = filterFromString(filterName);
              const filterValue = filter.getPickerFunc()(element) as
                | string
                | string[];
              const filterLabel = filter.getLabelPickerFunc()(element);

              if (typeof filterValue === 'string') {
                if (filterValue) {
                  updateValueMap(filterName, filterValue, filterLabel);
                }
              } else if (Array.isArray(filterValue)) {
                filterValue.forEach(val => {
                  if (val) {
                    updateValueMap(filterName, val, filterLabel);
                  }
                });
              }
            });
        });

        queriesAdapter.addMany(
          state.filters.queries,
          Object.values(valueMap['queries'])
        );
        authorAdapter.addMany(
          state.filters.authors,
          Object.values(valueMap['authors'])
        );
        storesAdapter.addMany(
          state.filters.stores,
          Object.values(valueMap['stores'])
        );
        statusAdapter.addMany(
          state.filters.status,
          Object.values(valueMap['status'])
        );
        repetitionTypeAdapter.addMany(
          state.filters.repetitionType,
          Object.values(valueMap['repetitionType'])
        );

        state.currentList = prepareScheduleList(
          state.allSchedules,
          state.searchText,
          state.selectedFilters
        );
        state.shouldReloadList = false;
        state.appliedFilters = initialState.appliedFilters;
        state.appliedSearchText = initialState.searchText;
      })
      .addCase(fetchSchedules.rejected, (state, action) => {
        const payload = action.payload as
          | { message?: string; errorCode?: string }
          | number
          | undefined;

        if (
          typeof payload === 'object' &&
          payload !== null &&
          (payload.message === 'AUTH_REDIRECT_IN_PROGRESS' ||
            payload.errorCode === 'AUTH_REDIRECT_IN_PROGRESS')
        ) {
          // Interactive auth redirect is underway; keep loading state until navigation.
          state.status = 'loading';
          state.error = '';
          return;
        }

        state.status = 'error';
        state.error = action.payload;
      });
  },
});

const resetToBaseOccurences = (
  state: EntityState<FilterValue, string>,
  adapter: EntityAdapter<FilterValue, string>
) => {
  const ids = adapter.getSelectors().selectIds(state);
  adapter.updateMany(
    state,
    ids.map(id => {
      return {
        id: id,
        changes: {
          occurences: adapter.getSelectors().selectById(state, id)
            ?.baseOccurences,
        },
      };
    })
  );
};

const countFilterValues = (
  schedules: JobOverview[],
  idPicker: (schedule: JobOverview) => string | string[]
) => {
  const valueMap: Record<string, number> = {};
  const updateValueMap = (valueId: string) => {
    valueMap[valueId] = valueMap[valueId] ? valueMap[valueId] + 1 : 1;
  };
  schedules.forEach(schedule => {
    const valueId = idPicker(schedule);
    if (typeof valueId == 'string') {
      updateValueMap(valueId);
    } else {
      valueId.forEach(val => updateValueMap(val));
    }
  });

  return valueMap;
};

const updateFilterOcurences = (
  allSchedules: JobOverview[],
  toFilterBy: (j: JobOverview[]) => JobOverview[],
  adapter: EntityAdapter<FilterValue, string>,
  valuePicker: (j: JobOverview) => string | string[],
  entityState: EntityState<FilterValue, string>,
  ...otherFiltersValues: string[][]
) => {
  const ids = adapter.getSelectors().selectIds(entityState);
  if (
    otherFiltersValues.some((filterValues: string[]) => filterValues.length > 0) // if any of the other filters are selected, count the occurences, otherwise we just return base occurences
  ) {
    adapter.updateMany(
      entityState,
      ids.map(id => {
        return { id: id, changes: { occurences: 0 } };
      })
    );
    const filteredByOtherFilters = toFilterBy(allSchedules);
    const valueMap: Record<string, number> = countFilterValues(
      filteredByOtherFilters,
      valuePicker
    );

    adapter.updateMany(
      entityState,
      Object.keys(valueMap).map((key: string) => {
        return {
          id: key,
          changes: { occurences: valueMap[key] },
        };
      })
    );
  } else {
    adapter.updateMany(
      entityState,
      ids.map(id => {
        return {
          id: id,
          changes: {
            occurences: adapter.getSelectors().selectById(entityState, id)
              ?.baseOccurences,
          },
        };
      })
    );
  }
};

export const fetchSchedules = createAsyncThunk<JobOverview[]>(
  'schedules/fetch',
  async (_, thunkApi) => {
    const serializeError = (error: unknown) => {
      if (typeof error === 'string') {
        return { message: error };
      }

      if (error instanceof Error) {
        const serializableError: Record<string, string | undefined> = {
          name: error.name,
          message: error.message,
        };

        const errorCode = (error as { errorCode?: unknown }).errorCode;
        const subError = (error as { subError?: unknown }).subError;

        if (typeof errorCode === 'string') {
          serializableError.errorCode = errorCode;
        }
        if (typeof subError === 'string') {
          serializableError.subError = subError;
        }

        return serializableError;
      }

      return { message: 'Unknown schedules fetch error' };
    };

    try {
      const response = await jobService.getAll();
      if (response.status !== 200) {
        return thunkApi.rejectWithValue(response.status);
      }
      return response.json();
    } catch (error) {
      return thunkApi.rejectWithValue(serializeError(error));
    }
  }
);

export const {
  fetchNewSchedules,
  filterToggled,
  removeActiveFilter,
  filtersReset,
  searchtTextChanged,
  reloadList,
  reloadFilterValues,
  calculateFilterValuesOccurences,
  sortingChanged,
  pagingChanged,
  scheduleStatusChanged,
  scheduleDeleted,
} = scheduleListSlice.actions;

export default scheduleListSlice;

export const selectSchedules = (state: RootState): JobOverview[] =>
  state.schedules.currentList;

const selectScheduleStatus = (state: RootState) => state.schedules.status;
const selectScheduleError = (state: RootState) => state.schedules.error;

export const selectScheduleFetchState = createSelector(
  [selectScheduleStatus, selectScheduleError],
  (status, error) => {
    return { status, error };
  }
);

export const filterSelectors = (): FilterSelectors => {
  return {
    queries: queriesAdapter.getSelectors<RootState>(
      state => state.schedules.filters.queries
    ),
    stores: storesAdapter.getSelectors<RootState>(
      state => state.schedules.filters.stores
    ),
    authors: authorAdapter.getSelectors<RootState>(
      state => state.schedules.filters.authors
    ),
    status: statusAdapter.getSelectors<RootState>(
      state => state.schedules.filters.status
    ),
    repetitionType: repetitionTypeAdapter.getSelectors<RootState>(
      state => state.schedules.filters.repetitionType
    ),
  };
};

export const activeFiltersSelector = (state: RootState, type: string) => {
  return state.schedules.selectedFilters[type as keyof ActiveFilters];
};

export const sortingSelector = (state: RootState) => {
  return state.schedules.sortingState;
};

export const searchTextSelector = (state: RootState) => {
  return state.schedules.searchText;
};

export const pagingSelector = (state: RootState) => {
  return state.schedules.pagingState;
};

export const filterActivitySelector = (
  state: RootState,
  type: string,
  value?: string
) => {
  return state.schedules.selectedFilters[type as keyof ActiveFilters].some(
    filterValue => filterValue === value
  );
};

export const allActiveFiltersSelector = (state: RootState) => {
  return state.schedules.selectedFilters;
};

const selectScheduleId = (_state: RootState, scheduleId: number) => scheduleId;
export const selectScheduleById = createSelector(
  [selectSchedules, selectScheduleId],
  (schedules, scheduleId) =>
    schedules.find(schedule => schedule.id === scheduleId)
);

startAppListening({
  type: 'schedules/reloadFilterValues',
  effect: (_action, listenerApi) => {
    listenerApi.dispatch(calculateFilterValuesOccurences());
  },
});
